import { Link as RouterLink } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function BeginnerTutorial() {
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'Superbuy Spreadsheet Beginner Tutorial: Your First 10 Minutes on the Platform',
    description: 'A friendly step-by-step walkthrough for absolute beginners. Learn how to navigate categories, compare products, and make your first informed purchase using Superbuy Spreadsheet.',
    datePublished: '2026-07-13',
    dateModified: '2026-07-13',
    author: { '@type': 'Organization', name: 'Superbuy Spreadsheet' },
    publisher: { '@type': 'Organization', name: 'Superbuy Spreadsheet' },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'Do I need any prior shopping experience to use Superbuy Spreadsheet?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Not at all. The Superbuy Spreadsheet platform is designed specifically to be accessible for complete beginners. The intuitive category system, clear navigation, and step-by-step guides mean you can start discovering products within minutes of your first visit, regardless of prior shopping experience.',
        },
      },
      {
        '@type': 'Question',
        name: 'What should I do first when I visit the Superbuy Spreadsheet website?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Start by browsing the homepage to understand the available product categories. Pick one category that interests you — shoes, clothing, or accessories are great starting points — and click through to explore the curated listings. There is no account creation required, so you can begin discovering products immediately.',
        },
      },
      {
        '@type': 'Question',
        name: 'How long does it take to learn how to use Superbuy Spreadsheet effectively?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Most beginners feel comfortable navigating the platform within 10 to 15 minutes. The interface is intentionally straightforward — browse categories, review listings, compare options, and explore guides. There is no complex setup or learning curve, just intuitive browsing and discovery.',
        },
      },
    ],
  };

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

        <article className="py-10 md:py-14">
          <div className="w-full max-w-3xl mx-auto px-4 md:px-6">

            <div className="mb-8">
              <RouterLink to="/" className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium mb-6 whitespace-nowrap">
                <i className="ri-arrow-left-line"></i>
                Back to Home
              </RouterLink>
              <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">
                Beginner Tutorial
              </span>
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 leading-tight mb-4">
                Superbuy Spreadsheet Beginner Tutorial: Your First 10 Minutes on the Platform
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
                <span className="flex items-center gap-1.5"><i className="ri-time-line"></i>8 min read</span>
                <span className="flex items-center gap-1.5"><i className="ri-calendar-line"></i>Updated July 2026</span>
                <span className="flex items-center gap-1.5"><i className="ri-user-line"></i>Superbuy Spreadsheet Team</span>
              </div>
            </div>

            <div className="prose-custom space-y-8 text-foreground-700 leading-relaxed">

              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                  Welcome! Let Us Walk You Through Your First Visit
                </h2>
                <p className="text-base mb-4">
                  If you have just discovered the <strong>Superbuy Spreadsheet</strong> platform and are not quite sure where to start, you are in exactly the right place. This beginner tutorial is designed to take you from confused newcomer to confident browser in about ten minutes — no prior experience required, no account to create, and absolutely no complexity to wrestle with.
                </p>
                <p className="text-base mb-4">
                  The beauty of the Superbuy Spreadsheet approach is its simplicity. Unlike traditional shopping platforms that bombard you with pop-ups, recommendations, and confusing navigation menus, everything here is organized into <strong>clear, logical categories</strong> that make sense the moment you see them. You will not find yourself clicking through endless submenus or wondering where to go next.
                </p>
                <p className="text-base">
                  Think of this tutorial as a friendly companion sitting beside you, pointing out what each section does and how to get the most value from your browsing time. By the end, you will know exactly how to navigate the <RouterLink to="/" className="text-primary-600 hover:text-primary-700 font-medium">Superbuy Spreadsheet homepage</RouterLink>, explore categories, compare products, and make informed shopping decisions with confidence.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                  Minute 1-3: Understanding the Homepage Layout
                </h2>
                <p className="text-base mb-4">
                  When you first land on the Superbuy Spreadsheet homepage, take a moment to absorb the layout before clicking anything. At the very top, you will see the main headline introducing the platform, followed by two prominent buttons: <strong>Superbuy Sheet</strong> and <strong>Buy It Now</strong>. Both lead to the main shopping website where actual purchases happen — think of them as your exit doors to the product marketplace.
                </p>
                <p className="text-base mb-4">
                  Scroll down just a little, and you will encounter the <strong>category dropdown menu</strong>. This is your primary navigation tool. Click it, and you will see fourteen product categories ranging from Shoes and Hoodies to Accessories and Perfumes. Each category name is descriptive and self-explanatory — if you want shoes, click Shoes. If you want bags, click Bags. No guessing, no decoding, no confusion.
                </p>
                <p className="text-base mb-4">
                  Below the dropdown, you will find the same categories displayed as <strong>visual card buttons</strong>. These serve the same purpose as the dropdown but with a more visual, browsable format. Some people prefer the dropdown for speed, others prefer the cards for the visual experience — both lead to the same category pages on the main website.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                  Minute 4-6: Exploring Your First Product Category
                </h2>
                <p className="text-base mb-4">
                  Now for the fun part — let us explore your first category. Pick whichever one interests you most. For this tutorial, let us say you choose <strong>Shoes</strong>. Click the Shoes card (or select it from the dropdown), and a new browser tab will open taking you directly to the curated shoe listings on the main website.
                </p>
                <p className="text-base mb-4">
                  What you will see is an organized collection of footwear listings. Unlike a generic search results page where products are scattered randomly, these listings follow a consistent format that makes it easy to quickly scan multiple options. Pay attention to the product images, key details like sizes and styles, and any special characteristics that matter to you personally.
                </p>
                <p className="text-base mb-4">
                  Here is a beginner tip that experienced shoppers use: <strong>open two or three categories at once</strong> in separate tabs. For example, open Shoes in one tab, Accessories in another, and Bags in a third. This lets you cross-reference and discover complementary items — the perfect sneakers might pair beautifully with a bag you find in a neighboring category.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                  Minute 7-8: Comparing Products Like a Pro
                </h2>
                <p className="text-base mb-4">
                  Comparison is where the Superbuy Spreadsheet approach truly shines. Most beginners make the mistake of evaluating products one at a time in isolation, which makes it nearly impossible to judge relative value. The organized format encourages a <strong>side-by-side comparison mindset</strong> instead.
                </p>
                <p className="text-base mb-4">
                  When you have found a few products that catch your eye, take a moment to compare them using a simple mental checklist: Which one has the style you prefer? How do the details compare? Does one option clearly offer better value? Which one fits your budget better? The consistent information format across all listings makes this comparison process feel natural rather than forced.
                </p>
                <div className="p-5 rounded-lg bg-background-100 border border-background-200/70 my-6">
                  <h4 className="font-heading text-base font-semibold text-foreground-900 mb-3">Beginner Comparison Checklist</h4>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line text-primary-500 mt-0.5"></i><span>Open 3-5 products that interest you across multiple browser tabs</span></li>
                    <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line text-primary-500 mt-0.5"></i><span>Scan each one quickly — your first impression is often the most honest</span></li>
                    <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line text-primary-500 mt-0.5"></i><span>Narrow down to your top two and compare them in detail</span></li>
                    <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line text-primary-500 mt-0.5"></i><span>Trust your preferences — the best product is the one you will actually enjoy</span></li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                  Minute 9-10: Making Your First Informed Purchase
                </h2>
                <p className="text-base mb-4">
                  You have browsed categories, compared products, and narrowed down your choices. Now comes the final step — making your purchase on the main website. Remember that the <strong>Superbuy Spreadsheet platform</strong> is your discovery and comparison tool. The actual transaction happens on the main shopping website, which handles all payment processing, order tracking, and shipping logistics.
                </p>
                <p className="text-base mb-4">
                  Before completing your purchase, do one final review: double-check the product details, confirm the size or variant you want is available, and make sure you are comfortable with the pricing. The research you have done using the organized Superbuy Spreadsheet listings means you are making this decision with far more information than a typical impulse purchase.
                </p>
                <p className="text-base mb-6">
                  Congratulations! You have just completed your first Superbuy Spreadsheet shopping journey — from discovery to informed decision, all in about ten minutes. The more you use the platform, the faster and more intuitive this process becomes. Return to the <RouterLink to="/" className="text-primary-600 hover:text-primary-700 font-medium">homepage</RouterLink> anytime to explore new categories or check for updated listings.
                </p>

                <div className="flex flex-col sm:flex-row items-center gap-4 p-6 rounded-xl bg-primary-50 border border-primary-200">
                  <div className="flex-1 text-center sm:text-left">
                    <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-1">Ready to Try It Yourself?</h3>
                    <p className="text-sm text-foreground-600">Head back to the homepage and pick a category to start your first browsing session.</p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="px-6 py-3 rounded-lg bg-primary-500 text-white text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-all duration-200 text-center">
                      Start Shopping
                      <i className="ri-arrow-right-up-line ml-1.5"></i>
                    </a>
                    <RouterLink to="/" className="px-6 py-3 rounded-lg bg-background-50 border border-background-200 text-foreground-800 text-sm font-semibold whitespace-nowrap hover:bg-background-100 transition-all duration-200 text-center">
                      Back to Home
                    </RouterLink>
                  </div>
                </div>
              </section>

            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}